import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminPartnersApi, type PartnerUser, type PartnerTenant } from '@/lib/api';
import { Spinner } from '@/components/ui/Spinner';
import { toast } from '@/components/ui/toast';
import {
  ArrowLeft, Building2, Users, Globe, Zap,
  Calendar, UserPlus, Trash2, X, CheckCircle, Clock,
} from 'lucide-react';

export const Route = createFileRoute('/_auth/partner/$partnerId')({
  component: PartnerDetailPage,
});

export default function PartnerDetailPage() {
  const { partnerId } = Route.useParams();
  const qc = useQueryClient();
  const [activeTab, setActiveTab] = useState<'tenants' | 'users'>('tenants');
  const [showInvite, setShowInvite] = useState(false);

  const { data: partner, isLoading } = useQuery({
    queryKey: ['admin', 'partners', partnerId],
    queryFn: () => adminPartnersApi.get(partnerId),
  });

  const { mutate: toggleStatus, isPending: togglingStatus } = useMutation({
    mutationFn: () => partner?.status === 'SUSPENDED'
      ? adminPartnersApi.activate(partnerId)
      : adminPartnersApi.suspend(partnerId),
    onSuccess: (p) => {
      toast.success(p.status === 'ACTIVE' ? 'Partner reactivated' : 'Partner suspended');
      qc.invalidateQueries({ queryKey: ['admin', 'partners', partnerId] });
    },
  });

  if (isLoading) {
    return <div style={{ padding: '60px 0', textAlign: 'center' }}><Spinner size="lg" /></div>;
  }
  if (!partner) {
    return (
      <div style={{ padding: '60px 0', textAlign: 'center', color: '#475569' }}>
        <p style={{ margin: 0, fontSize: 14 }}>Partner not found or API is offline.</p>
        <Link to="/partners" style={{ fontSize: 13, color: '#6366f1', marginTop: 8, display: 'inline-block' }}>← Back to Partners</Link>
      </div>
    );
  }

  const isSuspended = partner.status === 'SUSPENDED';

  return (
    <div className="page">
      {/* ── Back ── */}
      <Link to="/partners" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#475569', fontSize: 13, textDecoration: 'none', marginBottom: 20 }}>
        <ArrowLeft size={14} /> Back to partners
      </Link>

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{
            width: 48, height: 48, borderRadius: 12,
            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 700, fontSize: 20, color: '#fff', flexShrink: 0,
          }}>
            {partner.name[0].toUpperCase()}
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#e2e8f0' }}>{partner.name}</h1>
              <span style={{
                padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                background: isSuspended ? '#ef444420' : '#10b98120',
                color: isSuspended ? '#f87171' : '#34d399',
                border: `1px solid ${isSuspended ? '#ef444440' : '#10b98140'}`,
              }}>
                {isSuspended ? 'Suspended' : 'Active'}
              </span>
            </div>
            <p style={{ margin: '3px 0 0', color: '#475569', fontSize: 13 }}>@{partner.slug}</p>
          </div>
        </div>

        <button
          onClick={() => toggleStatus()}
          disabled={togglingStatus}
          style={{
            padding: '9px 18px', borderRadius: 9, fontSize: 13, fontWeight: 600, cursor: 'pointer',
            border: '1px solid', display: 'flex', alignItems: 'center', gap: 6,
            borderColor: isSuspended ? '#10b98140' : '#ef444440',
            background: 'transparent',
            color: isSuspended ? '#34d399' : '#f87171',
          }}
        >
          {togglingStatus ? <Spinner size="sm" /> : null}
          {isSuspended ? 'Reactivate partner' : 'Suspend partner'}
        </button>
      </div>

      {/* ── Info cards ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12, marginTop: 20 }}>
        <InfoCard icon={<Building2 size={15} />} label="Managed tenants" value={String(partner.tenantCount)} />
        <InfoCard icon={<Users size={15} />} label="Team members" value={String(partner.userCount)} />
        <InfoCard icon={<Globe size={15} />} label="Allowed domain" value={partner.allowedDomain ?? 'Not set'} />
        <InfoCard icon={<Zap size={15} />} label="Auto-provision" value={partner.autoProvision ? 'Enabled' : 'Disabled'} />
        <InfoCard icon={<Calendar size={15} />} label="Created" value={new Date(partner.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })} />
      </div>

      {/* ── Tabs ── */}
      <div style={{ display: 'flex', gap: 0, marginTop: 28, borderBottom: '1px solid #1a1f35' }}>
        {([
          { key: 'tenants', label: `Tenants (${partner.tenantCount})`, icon: <Building2 size={13} /> },
          { key: 'users', label: `Team (${partner.userCount})`, icon: <Users size={13} /> },
        ] as const).map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '10px 18px', background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 13, fontWeight: 600,
              color: activeTab === tab.key ? 'var(--color-primary)' : '#475569',
              borderBottom: `2px solid ${activeTab === tab.key ? 'var(--color-primary)' : 'transparent'}`,
              marginBottom: -1,
            }}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* ── Tenants tab ── */}
      {activeTab === 'tenants' && (
        <div style={{ marginTop: 16 }}>
          {partner.tenants.length === 0 ? (
            <EmptyState icon={<Building2 size={28} />} message="No tenants managed by this partner yet." />
          ) : (
            <div style={{ border: '1px solid #1a1f35', borderRadius: 12, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #1a1f35' }}>
                    {['Tenant', 'Plan', 'Status', 'Created'].map((h) => (
                      <th key={h} style={{ padding: '10px 16px', textAlign: 'left', color: '#475569', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {partner.tenants.map((t) => <TenantRow key={t.id} tenant={t} />)}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Users tab ── */}
      {activeTab === 'users' && (
        <div style={{ marginTop: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
            <button
              id="invite-partner-user-btn"
              onClick={() => setShowInvite(true)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px',
                background: 'var(--color-primary)', color: '#fff', border: 'none',
                borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
              }}
            >
              <UserPlus size={14} /> Invite team member
            </button>
          </div>

          {partner.users.length === 0 ? (
            <EmptyState icon={<Users size={28} />} message="No team members yet. Invite the first one." />
          ) : (
            <div style={{ border: '1px solid #1a1f35', borderRadius: 12, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #1a1f35' }}>
                    {['User', 'Role', 'Status', 'Last login', ''].map((h) => (
                      <th key={h} style={{ padding: '10px 16px', textAlign: 'left', color: '#475569', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {partner.users.map((u) => (
                    <UserRow
                      key={u.id}
                      user={u}
                      onRemove={() => qc.invalidateQueries({ queryKey: ['admin', 'partners', partnerId] })}
                      partnerId={partnerId}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {showInvite && (
        <InviteUserDrawer
          partnerId={partnerId}
          onClose={() => setShowInvite(false)}
          onInvited={() => {
            setShowInvite(false);
            qc.invalidateQueries({ queryKey: ['admin', 'partners', partnerId] });
          }}
        />
      )}
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function InfoCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div style={{
      padding: '14px 16px', background: '#0a0e1c',
      border: '1px solid #1a1f35', borderRadius: 10,
      display: 'flex', gap: 12, alignItems: 'flex-start',
    }}>
      <span style={{ color: '#6366f1', paddingTop: 2 }}>{icon}</span>
      <div>
        <p style={{ margin: 0, fontSize: 11, color: '#475569', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</p>
        <p style={{ margin: '3px 0 0', fontSize: 13, color: '#e2e8f0', fontWeight: 600 }}>{value}</p>
      </div>
    </div>
  );
}

function EmptyState({ icon, message }: { icon: React.ReactNode; message: string }) {
  return (
    <div style={{ padding: '48px 0', textAlign: 'center', color: '#475569', border: '1px dashed #1e2130', borderRadius: 12 }}>
      <span style={{ opacity: 0.3, display: 'block', marginBottom: 10 }}>{icon}</span>
      <p style={{ margin: 0, fontSize: 13 }}>{message}</p>
    </div>
  );
}

function TenantRow({ tenant }: { tenant: PartnerTenant }) {
  const STATUS_COLOR: Record<string, string> = { ACTIVE: '#34d399', TRIAL: '#fbbf24', SUSPENDED: '#f87171', ARCHIVED: '#475569' };
  const PLAN_COLOR: Record<string, string> = { ENTERPRISE: '#a78bfa', PROFESSIONAL: '#6366f1', STARTER: '#38bdf8', TRIAL: '#475569' };
  return (
    <tr style={{ borderBottom: '1px solid #12172a' }}>
      <td style={{ padding: '12px 16px' }}>
        <p style={{ margin: 0, fontWeight: 600, color: '#e2e8f0' }}>{tenant.name}</p>
        <p style={{ margin: 0, fontSize: 11, color: '#475569' }}>@{tenant.slug}</p>
      </td>
      <td style={{ padding: '12px 16px' }}>
        <span style={{ padding: '3px 9px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: `${PLAN_COLOR[tenant.plan] ?? '#475569'}20`, color: PLAN_COLOR[tenant.plan] ?? '#475569', border: `1px solid ${PLAN_COLOR[tenant.plan] ?? '#475569'}40` }}>
          {tenant.plan}
        </span>
      </td>
      <td style={{ padding: '12px 16px' }}>
        <span style={{ padding: '3px 9px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: `${STATUS_COLOR[tenant.status] ?? '#475569'}20`, color: STATUS_COLOR[tenant.status] ?? '#475569', border: `1px solid ${STATUS_COLOR[tenant.status] ?? '#475569'}40` }}>
          {tenant.status}
        </span>
      </td>
      <td style={{ padding: '12px 16px', color: '#64748b', fontSize: 12 }}>
        {new Date(tenant.createdAt).toLocaleDateString('en-GB')}
      </td>
    </tr>
  );
}

function UserRow({ user, partnerId, onRemove }: { user: PartnerUser; partnerId: string; onRemove: () => void }) {
  const { mutate: remove, isPending } = useMutation({
    mutationFn: () => adminPartnersApi.removeUser(partnerId, user.id),
    onSuccess: () => { toast.success(`${user.email} removed`); onRemove(); },
    onError: (e: Error) => toast.error(e?.message ?? 'Cannot remove user'),
  });

  const displayName = user.firstName && user.lastName
    ? `${user.firstName} ${user.lastName}`
    : user.name ?? user.email;

  return (
    <tr style={{ borderBottom: '1px solid #12172a' }}>
      <td style={{ padding: '12px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 30, height: 30, borderRadius: 8, background: '#1e2130', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 12, color: '#818cf8' }}>
            {displayName[0].toUpperCase()}
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <p style={{ margin: 0, fontWeight: 600, color: '#e2e8f0', fontSize: 13 }}>{displayName}</p>
              {user.isDefault && (
                <span style={{ padding: '1px 7px', borderRadius: 20, fontSize: 10, fontWeight: 700, background: '#6366f120', color: '#818cf8', border: '1px solid #6366f140' }}>Default</span>
              )}
            </div>
            <p style={{ margin: 0, fontSize: 11, color: '#475569' }}>{user.email}</p>
          </div>
        </div>
      </td>
      <td style={{ padding: '12px 16px' }}>
        <span style={{ padding: '3px 9px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: '#6366f120', color: '#818cf8', border: '1px solid #6366f140' }}>
          Admin
        </span>
      </td>
      <td style={{ padding: '12px 16px' }}>
        {user.isActive
          ? <span style={{ display: 'flex', alignItems: 'center', gap: 5, color: '#34d399', fontSize: 12 }}><CheckCircle size={12} /> Active</span>
          : <span style={{ display: 'flex', alignItems: 'center', gap: 5, color: '#64748b', fontSize: 12 }}><Clock size={12} /> Pending invite</span>
        }
      </td>
      <td style={{ padding: '12px 16px', color: '#64748b', fontSize: 12 }}>
        {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleDateString('en-GB') : 'Never'}
      </td>
      <td style={{ padding: '12px 16px' }}>
        <button
          onClick={() => remove()}
          disabled={isPending || user.isDefault}
          style={{ background: 'none', border: 'none', color: user.isDefault ? '#334155' : '#ef4444', cursor: user.isDefault ? 'not-allowed' : 'pointer', padding: 4, opacity: isPending ? 0.5 : 1 }}
          title={user.isDefault ? 'Cannot remove default contact — reassign first' : 'Remove user'}
        >
          <Trash2 size={14} />
        </button>
      </td>
    </tr>
  );
}

function InviteUserDrawer({ partnerId, onClose, onInvited }: {
  partnerId: string; onClose: () => void; onInvited: () => void;
}) {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [receiveAlerts, setReceiveAlerts] = useState(false);

  const isValid = email.trim().includes('@') && firstName.trim() && lastName.trim();

  const { mutate, isPending } = useMutation({
    mutationFn: () => adminPartnersApi.inviteUser(partnerId, {
      email: email.trim(),
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      role: 'ADMIN',
      receiveAlerts,
    }),
    onSuccess: () => { toast.success(`Invitation sent to ${email}`); onInvited(); },
  });

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', zIndex: 40 }} />
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: '100%', maxWidth: 400,
        background: '#0d1022', borderLeft: '1px solid #1e2130', zIndex: 50,
        padding: '28px 24px', overflowY: 'auto',
        boxShadow: '-16px 0 60px rgba(0,0,0,0.6)',
        animation: 'drawerIn 0.22s cubic-bezier(0.34,1.2,0.64,1)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#e2e8f0' }}>Invite Admin</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#475569', cursor: 'pointer', padding: 4 }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
          <div>
            <label style={labelStyle}>First Name <span style={{ color: '#f87171' }}>*</span></label>
            <input id="invite-first-name" value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="Alice" autoFocus style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Last Name <span style={{ color: '#f87171' }}>*</span></label>
            <input id="invite-last-name" value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Virtanen" style={inputStyle} />
          </div>
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={labelStyle}>Work Email <span style={{ color: '#f87171' }}>*</span></label>
          <input id="invite-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="alice@acme.com" style={inputStyle} />
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: 24 }}>
          <input
            id="invite-receive-alerts"
            type="checkbox"
            checked={receiveAlerts}
            onChange={(e) => setReceiveAlerts(e.target.checked)}
            style={{ width: 15, height: 15, accentColor: 'var(--color-primary)', cursor: 'pointer' }}
          />
          <span style={{ fontSize: 13, color: '#94a3b8' }}>Receive platform alert notifications</span>
        </label>

        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, padding: '10px', background: 'transparent', border: '1px solid #1e2130', borderRadius: 9, color: '#64748b', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
          <button
            id="send-invite-btn"
            onClick={() => mutate()}
            disabled={!isValid || isPending}
            style={{ flex: 2, padding: '10px', background: 'var(--color-primary)', border: 'none', borderRadius: 9, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: !isValid ? 0.5 : 1 }}
          >
            {isPending ? <Spinner size="sm" /> : 'Send invitation'}
          </button>
        </div>
      </div>
    </>
  );
}

const labelStyle: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 600, color: '#94a3b8', marginBottom: 6 };
const inputStyle: React.CSSProperties = { width: '100%', padding: '9px 12px', background: '#0f1221', border: '1px solid #1e2130', borderRadius: 8, color: '#e2e8f0', fontSize: 13, outline: 'none', boxSizing: 'border-box' };
